/**
 * Ф3: секція «Версії бюджету місяця».
 *
 * Версії не правляться на місці — кожна редакція це окремий рядок (ТЗ B9), тож
 * список тут і є журналом: хто згенерував, що змінилось проти попередньої,
 * хто затвердив. Затвердження робиться звідси, а не з хедера: дія адресна —
 * затверджують КОНКРЕТНУ версію, а не «поточний стан».
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useState } from 'react';
import { GitBranch, Check, CircleSlash, FlaskConical } from 'lucide-react';
import { API_BASE_URL, authFetch } from '@/services/api';
import type { BudgetVersion } from '../types';
import { mln, n1 } from '../types';

interface VersionDiff {
  prevVersionId: string; prevStage: string;
  revenue: { from: number; to: number; delta: number };
  ebitda: { from: number; to: number; delta: number };
  exportMwh: { from: number; to: number; delta: number };
  netProfit: { from: number; to: number; delta: number };
}
export interface VersionRow extends BudgetVersion {
  diff?: VersionDiff | null;
}

const STATUS_LABEL: Record<string, string> = {
  get approved() { return i18n.t('ak_bud_approved'); },
  get pending() { return i18n.t('ak_bud_pending_approval'); },
  get superseded() { return i18n.t('ak_bud_superseded'); },
};
const STAGE_LABEL: Record<string, string> = {
  get draft() { return i18n.t('ak_bud_draft_t_5'); },
  get final() { return i18n.t('ak_bud_final_t_1'); },
  get imported() { return i18n.t('ak_bud_reference_import'); },
};

const dt = (iso: string | null) => (iso ? iso.slice(0, 16).replace('T', ' ') : '—');
const sgn = (v: number) => (v > 0 ? '+' : '');

export default function VersionsSection({ versions, month, onChanged }: {
  versions: VersionRow[]; month: string; onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string>('');
  const { t } = useTranslation();
  const mine = versions.filter((v) => v.kind === 'month' && v.period === month);

  const approve = async (id: string) => {
    if (busy) return;
    setBusy(id);
    try {
      const r = await authFetch(`${API_BASE_URL}/budget/version/${id}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      if (!r.ok) window.alert(t('ak_bud_failed_to_approve_http', { s: r.status }));
    } catch { window.alert(t('ak_bud_failed_to_approve_the_version')); }
    finally { setBusy(''); onChanged(); }
  };

  return (
    <div className="tile p-4">
      <div className="flex items-start justify-between gap-3 pb-3 mb-3"
        style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2">
          <GitBranch size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          <span className="text-[12.5px] font-semibold" style={{ color: 'var(--text)' }}>
            {t('ak_bud_monthly_budget_versions')}
          </span>
        </div>
        <span className="text-[11px] shrink-0 text-right" style={{ color: 'var(--text-dim)' }}>
          {t('ak_bud_versions_are_not_edited_each_revision')}
        </span>
      </div>

      {mine.length === 0 ? (
        <div className="text-[12.5px] py-5 text-center" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud_no_versions_for_the_month_yet_generate')}
        </div>
      ) : (
        mine.map((v) => {
          const isApproved = v.status === 'approved';
          const isSuperseded = v.status === 'superseded';
          return (
            <div key={v.id} className="py-2.5" style={{ borderTop: '1px solid var(--line)' }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12.5px] font-semibold" style={{ color: 'var(--text)' }}>
                      {STAGE_LABEL[v.stage] || v.stage}
                    </span>
                    {v.stress && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5"
                        style={{ color: 'var(--warn)', border: '1px solid var(--line)', borderRadius: 6 }}>
                        <FlaskConical size={10} strokeWidth={2} /> {t('ak_bud_stress')}
                      </span>
                    )}
                    <span className="text-[11px]"
                      style={{ color: isApproved ? 'var(--ok)' : isSuperseded ? 'var(--text-dim)' : 'var(--warn)' }}>
                      {STATUS_LABEL[v.status] || v.status}
                      {v.autoApproved ? t('ak_bud_auto') : ''}
                    </span>
                  </div>
                  <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
                    {t('ak_bud_created', { d: dt(v.createdAt) })}
                    {v.approvedBy ? t('ak_bud_approved_by', { by: v.approvedBy, d: dt(v.approvedAt) }) : ''}
                    {v.scenario ? t('ak_bud_scenario', { s: v.scenario }) : ''}
                  </div>
                  {v.diff && (
                    <div className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      {t('ak_bud_vs_previous', { s: v.diff.prevStage })}
                      {' '}{t('ak_bud_revenue_2')} <b style={{ color: v.diff.revenue.delta >= 0 ? 'var(--ok)' : 'var(--warn)' }}>
                        {sgn(v.diff.revenue.delta)}{mln(v.diff.revenue.delta)} {t('ak_bud_mn')}
                      </b>
                      {' · '}EBITDA {sgn(v.diff.ebitda.delta)}{mln(v.diff.ebitda.delta)} {t('ak_bud_mn')}
                      {' · '}{t('ak_bud_export')} {sgn(v.diff.exportMwh.delta)}{n1(v.diff.exportMwh.delta)} {t('ak_bud_mwh')}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {v.status === 'pending' ? (
                    <button onClick={() => approve(v.id)} disabled={!!busy}
                      className="inline-flex items-center gap-1.5 px-2.5 cursor-pointer"
                      style={{
                        height: 30, borderRadius: 8, fontSize: 11.5,
                        background: 'var(--surface-2)', border: '1px solid var(--line)',
                        color: 'var(--text)', opacity: busy ? 0.5 : 1,
                      }}
                      title={t('ak_bud_approve_this_version_the_other_version')}>
                      <Check size={13} strokeWidth={2} /> {t('ak_bud_approve')}
                    </button>
                  ) : isSuperseded ? (
                    <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-dim)' }}>
                      <CircleSlash size={12} strokeWidth={2} /> {t('ak_bud_inactive')}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })
      )}

      <div className="text-[10.5px] mt-3 pt-3"
        style={{ borderTop: '1px solid var(--line)', color: 'var(--text-dim)' }}>
        {t('ak_bud_spec_a2_rules_draft_t_5_final_t_1_at_0')}
      </div>
    </div>
  );
}
